import { useContext, useEffect } from "react";

import { NavigationType, useNavigate, useNavigationType } from "react-router";

import { css } from "@emotion/react";
import CircularProgress from "@mui/material/CircularProgress";

import { colors, MenuIcon } from "@dotnet/telematics-design-system";

import CompanyLevelDialog from "components/CompanyLevel";
import Backdrop from "components/DesignComponents/Backdrop";
import Button, {
    BUTTON_COLORS,
    BUTTON_SIZES,
    BUTTON_VARIANTS
} from "components/DesignComponents/Button";
import CompanyFilter from "components/Filters/CompanyFilter";
import FindTool from "components/Filters/FindTool";
import Logo from "components/Logo";
import { BURGER_BUTTON } from "components/TutorialMode/constants";
import AuthContext from "context/auth/authContext";
import { useBackgroundActions } from "context/notifications/hooks";
import ParamsContext from "context/params/paramsContext";
import RoutesContext from "context/routes/routesContext";
import { useMediaQueries } from "shared/hooks/useMediaQueries";
import { useParamsFromUrlAndBrowser } from "shared/hooks/useParamsFromUrlAndBrowser";
import { useWindowLocation } from "shared/hooks/useWindowLocation";
import { FILTER_NAMES, ROUTES } from "shared/types/enums";

import Controls from "./Controls";
import DesktopDrawer from "./DesktopDrawer/DesktopDrawer";
import { getCompanyFilteredValues, handleLogoClick } from "./functions";
import MobileDrawer from "./MobileDrawer";
import { NavbarProps } from "./types";

const Navbar = ({ bannerHeight }: NavbarProps) => {
    const { gray200, gray900, white } = colors;

    const { isLoading, isSupportUser } = useContext(AuthContext);

    const {
        filterDropdownOptions,
        queryParams,
        reloadRootCompany,
        setQueryParams
    } = useContext(ParamsContext);

    const {
        isCompanyLevelDialogOpen,
        toggleCompanyLevelDialogOpen,
        setMobileDrawerStatus
    } = useContext(RoutesContext);

    const { pathname } = useWindowLocation();
    const { toMd, fromMd } = useMediaQueries();
    const { currentParams } = useParamsFromUrlAndBrowser();
    useBackgroundActions();

    const navigate = useNavigate();
    const navigationType = useNavigationType();

    const rootCompanyId = queryParams[FILTER_NAMES.RootCompany] as
        string | undefined;

    useEffect(() => {
        if (pathname !== ROUTES.Login) {
            const companyFilteredValues = getCompanyFilteredValues(
                filterDropdownOptions,
                currentParams
            );

            if (rootCompanyId) {
                const isPopAction = navigationType === NavigationType.Pop;

                setQueryParams({
                    params: {
                        ...currentParams,
                        // when using the browser's back/forward buttons
                        ...(isPopAction
                            ? {}
                            : {
                                  [FILTER_NAMES.RootCompany]: rootCompanyId
                              })
                    },
                    filteredValues: companyFilteredValues,
                    resetParams: true
                });
            }
        }

        // eslint-disable-next-line
    }, [rootCompanyId, pathname]);

    useEffect(() => {
        setMobileDrawerStatus(false);
        // eslint-disable-next-line
    }, [pathname, fromMd]);

    return (
        <>
            <header
                css={css({
                    position: "sticky",
                    top: "0",
                    zIndex: "2",
                    background: white
                })}
            >
                <div
                    css={css({
                        padding: "10px 24px 9px 24px",
                        borderBottom: `1px solid ${gray200}`,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center"
                    })}
                >
                    <Logo
                        css={css({
                            cursor: "pointer"
                        })}
                        onClick={() => handleLogoClick(pathname, navigate)}
                    />

                    <div
                        css={css({
                            display: "flex",
                            alignItems: "center"
                        })}
                    >
                        {isSupportUser && (
                            <FindTool
                                css={css({
                                    marginRight: toMd ? "4px" : "8px"
                                })}
                            />
                        )}

                        <CompanyFilter
                            key={`company-filter-${reloadRootCompany}`}
                        />

                        {toMd ? (
                            <Button
                                id={BURGER_BUTTON}
                                color={BUTTON_COLORS.White}
                                size={BUTTON_SIZES.Small}
                                variant={BUTTON_VARIANTS.IconOnly}
                                icon={
                                    <MenuIcon
                                        css={css({
                                            color: gray900
                                        })}
                                    />
                                }
                                onClick={() => setMobileDrawerStatus(true)}
                                css={css({
                                    marginLeft: toMd ? "4px" : "8px"
                                })}
                            />
                        ) : (
                            <Controls />
                        )}
                    </div>
                </div>
            </header>

            {toMd ? (
                <MobileDrawer />
            ) : (
                <DesktopDrawer bannerHeight={bannerHeight} />
            )}

            <Backdrop open={isLoading}>
                <CircularProgress
                    sx={{
                        color: white
                    }}
                />
            </Backdrop>

            <CompanyLevelDialog
                isOpen={isCompanyLevelDialogOpen}
                close={() => toggleCompanyLevelDialogOpen(false, false)}
            />
        </>
    );
};

export default Navbar;
